import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ColumnDefinition, ColumnVisibility } from '../../../../core/model/column-definition';
import { SidebarItem } from '../../../model/selectable-items';
import { FormControlExFactory } from '../../../service/factory/form-control-ex-factory.service';
import { LanguageService } from '../../../service/language.service';

const SIDEBAR_FILTERS = 'filters';
const SIDEBAR_COLUMNS = 'columns';
type SidebarItemType = typeof SIDEBAR_FILTERS | typeof SIDEBAR_COLUMNS;

@Component({
    selector: 'app-list-management-sidebar',
    templateUrl: './list-management-sidebar.component.html',
    styleUrls: ['./list-management-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListManagementSidebarComponent implements OnInit, OnChanges {
  /** This is "readonly" input that it is copied to "sidebarItems" on every open (see ngOnChanges). */
  @Input() items: SidebarItem[];
  @Input() open: boolean;
  @Input() columnDefinition: ColumnDefinition[];
  @Input() defaultFilters: string[];
  @Input() type: SidebarItemType;
  @Input() title: string;
  @Input() searchCaption: string;
  @Input() searchFilterInput: string = '';
  @Input() validate: boolean = false;
  @Output() onSave = new EventEmitter<SidebarItem[]>();
  @Output() onCancel = new EventEmitter<void>();

  /** Local copy of the "items" input so the real items are unaffected unless the user decides to press Save. */
  sidebarItems: SidebarItem[];
  /** User can reorder items (determined by item type). */
  reorderable: boolean = false;
  allSelected = false;
  translationInstants: string[];

  form: FormGroup;

  constructor(
    private readonly _languageService: LanguageService,
    private readonly _fcf: FormControlExFactory,
    private readonly fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      searchFilter: this._fcf.create('SEARCH_COLUMNS', ''),
    });
  }

  ngOnInit() {
    this.reorderable = this.type === SIDEBAR_COLUMNS;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open']?.currentValue) {
      this.form.controls['searchFilter'].setValue(this.searchFilterInput ?? '');
      this.processItems();
      this.initialOrder();
    }
  }

  private initialOrder(): void {
    if (this.reorderable) this.orderSidebarSections();
  }

  sidebarSave(): void {
    this.onSave.emit(this.sidebarItems);
    this.sidebarCancel();
  }

  sidebarCancel(): void {
    this.onCancel.emit();
    this.searchFilterInput = '';
    this.form.controls['searchFilter'].setValue('');
  }

  restoreDefaults(): void {
    const items = this.sidebarItems;
    if (!items) return;

    switch (this.type) {
      // Columns are reorderable, so we need to restore both selection and order:
      case SIDEBAR_COLUMNS:
        const defaultColumns = this.getDefaultColumns();
        const findIndex = (items: SidebarItem[], binding: string) => items.findIndex(x => x.binding === binding);

        // data sanity checking:
        if (defaultColumns.length > items.length) {
          throw new Error('Default item count exceeds the numer of defined items');
        }

        // swap-sort items based on default item order:
        defaultColumns.forEach((key, correctIndex) => {
          const itemIndex = findIndex(items, key);
          if (itemIndex === -1) {
            console.warn(`Default item ${key} not found - skipping!`);
          } else {
            // swap correctIndex with itemIndex:
            const temp = items[correctIndex];
            items[correctIndex] = items[itemIndex];
            items[itemIndex] = temp;
          }
        });

        // update selected state:
        items.forEach((item, i) => (item.selected = i < defaultColumns.length));
        return;

      // Filter code is simpler, because filters are not reorderable:
      case SIDEBAR_FILTERS:
        if (this.defaultFilters) {
          items.forEach(item => (item.selected = this.defaultFilters.includes(item.name)));
        }
        return;

      default:
        console.error('Unknown sidebar type:', this.type);
        return;
    }
  }

  private getDefaultColumns(): string[] {
    return this.columnDefinition.filter(x => x.visibility !== ColumnVisibility.optional).map(x => x.binding);
  }

  get selectedItems(): SidebarItem[] {
    return this.sidebarItems?.filter(item => item.selected) || [];
  }

  get deselectedItems(): SidebarItem[] {
    if (!this.sidebarItems) return [];

    const deselected = this.sidebarItems?.filter(item => !item.selected);
    if (this.reorderable) this.alphabeticalSort(deselected);

    return deselected;
  }

  toggleAll(checked: boolean): void {
    this.sidebarItems.forEach(item => (item.selected = checked));
    this.allSelected = checked;
  }

  checkAllSelected(): boolean {
    this.allSelected = this.sidebarItems?.every(item => item.selected) ?? false;
    return this.allSelected;
  }

  setItemSelected(item: SidebarItem, checked: boolean): void {
    item.selected = checked;
    if (this.reorderable) this.orderSidebarSections();
  }

  isAnyCheckboxSelected(): boolean {
    return this.sidebarItems.some(item => item.selected);
  }

  orderSidebarSections(): void {
    const selected = this.selectedItems;
    const deselected = this.deselectedItems;
    this.sidebarItems = [...selected, ...deselected];
  }

  drop(event: CdkDragDrop<SidebarItem[]>) {
    const selectedColumns = this.selectedItems;
    const previousIndex = selectedColumns.findIndex(item => item === this.sidebarItems[event.previousIndex]);
    const currentIndex = selectedColumns.findIndex(item => item === this.sidebarItems[event.currentIndex]);

    moveItemInArray(selectedColumns, previousIndex, currentIndex);
    this.sidebarItems = [...selectedColumns, ...this.deselectedItems];
  }

  private alphabeticalSort(items: SidebarItem[]) {
    return items.sort((a, b) => {
      const captionA = this._languageService.instant(a.caption) as string;
      const captionB = this._languageService.instant(b.caption) as string;
      return captionA.localeCompare(captionB, this._languageService.currentLanguage);
    });
  }

  private processItems() {
    if (!this.items) {
      return;
    }

    if (this.type === SIDEBAR_COLUMNS) {
      this.sidebarItems = this.items.map(item => ({
        selected: item.selected,
        caption: item.caption,
        id: item.id,
        binding: item.binding,
      }));
    } else if (this.type === SIDEBAR_FILTERS) {
      this.sidebarItems = this.items.map(item => ({
        selected: item.selected,
        caption: item.caption,
        name: item.name,
      }));
    }
    this.translationInstants = [];
    this.sidebarItems.forEach(x => this.translationInstants.push(x.caption));
    this.setupTranslatedCaptions();
    if (!this.reorderable) this.alphabeticalSort(this.sidebarItems);
  }

  private setupTranslatedCaptions() {
    if (!this.sidebarItems) return;

    for (let i = 0; i < this.sidebarItems.length; ++i) {
      const item = this.sidebarItems[i];
      const caption = this._languageService.instant(this.translationInstants[i]);
      if (item.id || item.name) item.caption = caption;
    }
  }
}
